import type { NotificationDetailAction } from "./notificationDetailActions"
import { APP_ORIGIN } from "@/constants/brand"
import { deviceIanaTimeZone } from "./reportingTimeZone"

function pickStr(obj: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = obj[k]
    if (typeof v === "string" && v.trim()) return v.trim()
  }
  return undefined
}

function ymdFromIsoOrDate(raw: string): string | null {
  const t = raw.trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t
  const ms = Date.parse(t)
  if (Number.isNaN(ms)) return null
  return t.slice(0, 10)
}

function exclusiveEndToInclusiveYmd(exclusiveEnd: string): string | null {
  const ymd = ymdFromIsoOrDate(exclusiveEnd)
  if (!ymd) return null
  const [y, m, d] = ymd.split("-").map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() - 1)
  return dt.toISOString().slice(0, 10)
}

export type RecordTradingDayDeepLinks = {
  account: string
  localStart: string
  localEnd: string
  timezone: string
  statisticsPath: string
  workspaceReportPath: string
  tradeReviewPath: string
}

function buildQuery(params: {
  account: string
  localStart: string
  localEnd: string
  timezone: string
}): string {
  const q = new URLSearchParams()
  q.set("broker_account_code", params.account)
  q.set("local_start_date", params.localStart)
  q.set("local_end_date", params.localEnd)
  q.set("timezone", params.timezone)
  return q.toString()
}

/**
 * Resolve account + inclusive local day window for performance dive CTAs
 * (best/worst day). Also accepts `crossed_at` when present on other payloads.
 * Prefers `statistics_url` query params when present.
 */
export function resolveRecordTradingDayDeepLinks(
  details: unknown
): RecordTradingDayDeepLinks | null {
  if (!details || typeof details !== "object" || Array.isArray(details)) {
    return null
  }
  const obj = details as Record<string, unknown>

  let account = pickStr(obj, [
    "broker_account_code",
    "brokerAccountCode",
    "account_code",
    "accountCode",
  ])
  let localStart: string | null = null
  let localEnd: string | null = null
  let timezone =
    pickStr(obj, ["timezone", "time_zone", "timeZone"]) || deviceIanaTimeZone()

  const fromUrl = pickStr(obj, ["statistics_url", "statisticsUrl", "action_url", "actionUrl"])
  if (fromUrl) {
    try {
      const u = new URL(fromUrl, APP_ORIGIN)
      account = u.searchParams.get("broker_account_code") || account
      localStart = u.searchParams.get("local_start_date")
      localEnd = u.searchParams.get("local_end_date")
      timezone = u.searchParams.get("timezone") || timezone
    } catch {
      /* fall through */
    }
  }

  if (!localStart || !localEnd) {
    const periodLabel = pickStr(obj, ["period_label", "periodLabel", "period"])
    if (periodLabel) {
      const single = ymdFromIsoOrDate(periodLabel)
      if (single) {
        localStart = single
        localEnd = single
      }
    }
  }

  if (!localStart || !localEnd) {
    const crossed = pickStr(obj, ["crossed_at", "crossedAt"])
    if (crossed) {
      const single = ymdFromIsoOrDate(crossed)
      if (single) {
        localStart = single
        localEnd = single
      }
    }
  }

  if (!localStart || !localEnd) {
    const startRaw = pickStr(obj, [
      "period_start_local",
      "periodStartLocal",
      "period_start",
      "periodStart",
      "local_start_date",
      "localStartDate",
    ])
    const endRaw = pickStr(obj, [
      "period_end_local",
      "periodEndLocal",
      "period_end",
      "periodEnd",
      "local_end_date",
      "localEndDate",
    ])
    if (startRaw && endRaw) {
      localStart = ymdFromIsoOrDate(startRaw)
      const endLooksExclusive =
        Boolean(obj.period_end_local || obj.periodEndLocal) ||
        Boolean(obj.period_end || obj.periodEnd)
      localEnd = endLooksExclusive
        ? exclusiveEndToInclusiveYmd(endRaw)
        : ymdFromIsoOrDate(endRaw)
    }
  }

  if (!account || !localStart || !localEnd) return null

  const query = buildQuery({
    account,
    localStart,
    localEnd,
    timezone,
  })

  return {
    account,
    localStart,
    localEnd,
    timezone,
    statisticsPath: `/statistics?${query}`,
    workspaceReportPath: `/workspace?create=report&${query}`,
    tradeReviewPath: `/trade-log?tab=review&${query}`,
  }
}

export type BuildRecordTradingDayDetailActionsOptions = {
  /** Include Trade Review deep link (worst-day notifications). */
  includeTradeReview?: boolean
}

/** View statistics + generate report (+ optional trade review) for record-day detail. */
export function buildRecordTradingDayDetailActions(
  details: unknown,
  options?: BuildRecordTradingDayDetailActionsOptions
): NotificationDetailAction[] {
  const links = resolveRecordTradingDayDeepLinks(details)
  if (!links) return []

  const actions: NotificationDetailAction[] = [
    {
      id: "statistics",
      rowLabel: "Statistics",
      buttonLabel: "View statistics",
      path: links.statisticsPath,
    },
  ]

  if (options?.includeTradeReview) {
    actions.push({
      id: "trade-review",
      rowLabel: "Trade review",
      buttonLabel: "Open trade review",
      path: links.tradeReviewPath,
    })
  }

  actions.push({
    id: "reports",
    rowLabel: "Report",
    buttonLabel: "Generate report",
    path: links.workspaceReportPath,
  })

  return actions
}
